import { useIsAuthenticated } from '@/hooks';
import { useGetUserProfileQuery, useGetUserWalletsQuery } from '@/store/api/userApi';
import { formatCurrency } from '@/utils';
import { RefreshCw } from 'lucide-react';
import { GiHamburgerMenu } from 'react-icons/gi';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import SidebarMenu from './SidebarMenu';
import { useState } from 'react';

type Props = {};

const backButtonRoutes = ['/marketdetails', '/fullmarket/'];

function Header({}: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const showBackButton = backButtonRoutes.some((route) => pathname.startsWith(route));

  return (
    <>
      <div className="h-[3.125rem] py-1.5 px-1.5 sm:px-2 sticky top-0 z-30 flex justify-between items-center text-11 bg-primary ">
        <div className={'text-[1.75rem] flex items-center gap-2'}>
          {showBackButton ? (
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 items-center justify-center"
            >
              <i className="icon-arrow-down rotate-90 text-xl" />
            </button>
          ) : (
            isAuthenticated && (
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => setSidebarOpen(true)}
                className="flex h-8 w-8 items-center justify-center"
              >
                <GiHamburgerMenu />
              </button>
            )
          )}

          <Link to="/">
            <img src="/images/logo-black.webp" alt="" className="w-[6.5rem]" />
          </Link>
        </div>

        {isAuthenticated ? (
          <HeaderBalance />
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Agent List */}
            <Link
              to="#"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-[2rem] items-center justify-center rounded bg-black/40 px-2.5 sm:px-4 text-[0.938rem] sm:text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              <i className="icon-SignUp mr-1.5 text-base leading-none" />
              Agent List
            </Link>

            {/* Login */}
            <Link
              to={'/login'}
              className="flex h-[2rem] items-center justify-center rounded bg-[#e20000] px-3 sm:px-4 text-[0.938rem] sm:text-base font-semibold text-white transition-opacity hover:opacity-90"
            >
              <i className="icon-login mr-1.5 text-base leading-none" />
              Login
            </Link>
          </div>
        )}
      </div>
      <SidebarMenu open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </>
  );
}

export default Header;

const HeaderBalance = () => {
  const {
    data: profile,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useGetUserProfileQuery();
  const {
    data: wallet,
    isLoading: isWalletLoading,
    refetch: refetchWallet,
  } = useGetUserWalletsQuery(undefined, {
    pollingInterval: 1000,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const exposure = Math.abs(wallet?.exposure ?? 0);
  const mainBalance = (wallet?.mainBalance ?? 0) - exposure;
  const username = profile?.username ?? '';

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await Promise.allSettled([refetchProfile(), refetchWallet()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex items-center justify-between">
      {isProfileLoading || isWalletLoading || isRefreshing ? (
        <div className="flex items-center gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-black animate-dot-wave"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full w-full max-w-xs flex-col justify-between py-0.5 text-[0.844rem] leading-none">
          <p className="mb-1 text-header-text">{username ? `@${username}` : ''}</p>

          <div className="flex">
            <div>
              <span className="mr-1 font-bold text-black/50">PBU</span>
              <span>{formatCurrency(mainBalance)}</span>
            </div>

            <div className="pl-2.5">
              <span className="mr-1 font-bold text-black/50">Exp</span>
              <span className="text-rose-600">({formatCurrency(wallet?.exposure)})</span>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleRefresh}
        disabled={isRefreshing}
        className="ml-1 mr-1 flex cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-70"
      >
        <RefreshCw
          strokeWidth={2.8}
          className={`ml-2.5 mr-1 rotate-[130deg] size-[1.4rem] ${isRefreshing ? 'animate-spin' : ''}`}
        />
      </button>
    </div>
  );
};
